import { clsx } from "clsx";
import { AlertTriangle, Check, Info, Undo2, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { t, useLocale } from "@/shared/lib/i18n";

/**
 * Toasts.
 *
 * Used for two things only: confirming something that happened off-screen, and
 * offering to undo it. A toast that merely repeats what the person just watched
 * happen is noise, so most successful actions raise none at all.
 *
 * Undo is why this exists. Archiving, completing and rescheduling are all one
 * click away from being wrong, and an undo affordance is what lets those
 * actions stay one click instead of one click plus a confirmation.
 */

export type ToastTone = "neutral" | "success" | "warning" | "error";

export type ToastInput = {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** Milliseconds. Anything with an action gets longer by default. */
  duration?: number;
  action?: { label: string; onClick: () => void };
};

type Toast = ToastInput & { id: number };
type ToastListener = (input: ToastInput) => void;

const toastListeners = new Set<ToastListener>();

/** Allows the data layer to report a failed mutation without coupling every
 * query hook to React context. Providers subscribe when the application mounts. */
export function notifyToast(input: ToastInput): void {
  for (const listener of toastListeners) listener(input);
}

type ToastContextValue = {
  toast: (input: ToastInput) => void;
  /** Shorthand for the common "did a thing, here is how to take it back". */
  undoable: (title: string, onUndo: () => void, description?: string) => void;
  dismiss: (id: number) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const ICONS: Record<ToastTone, ReactNode> = {
  neutral: <Info className="size-4" />,
  success: <Check className="size-4" />,
  warning: <AlertTriangle className="size-4" />,
  error: <AlertTriangle className="size-4" />,
};

const TONES: Record<ToastTone, string> = {
  neutral: "text-ink-muted",
  success: "text-positive",
  warning: "text-caution",
  error: "text-critical",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  useLocale();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback(
    (input: ToastInput) => {
      const id = nextId.current++;
      setToasts((current) => [...current.slice(-3), { ...input, id }]);
      const duration = input.duration ?? (input.action ? 8000 : 4000);
      window.setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );

  const undoable = useCallback(
    (title: string, onUndo: () => void, description?: string) => {
      toast({
        title,
        description,
        tone: "neutral",
        action: { label: t("copy.6b65f1920bb2"), onClick: onUndo },
      });
    },
    [toast],
  );

  const value = useMemo(() => ({ toast, undoable, dismiss }), [toast, undoable, dismiss]);

  useEffect(() => {
    toastListeners.add(toast);
    return () => {
      toastListeners.delete(toast);
    };
  }, [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <section
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[1100] flex flex-col items-center gap-2 p-4 sm:items-end sm:p-6"
        aria-label={t("copy.fc2cbca9acbe")}
      >
        <AnimatePresence initial={false}>
          {toasts.map((item) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, y: 12, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.14 } }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className={clsx(
                "pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border border-line",
                "bg-surface-raised px-3.5 py-3 shadow-[var(--ex-shadow-overlay)]",
              )}
              role={item.tone === "error" ? "alert" : "status"}
              aria-live={item.tone === "error" ? "assertive" : "polite"}
            >
              <span className={clsx("mt-0.5 shrink-0", TONES[item.tone ?? "neutral"])}>
                {ICONS[item.tone ?? "neutral"]}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ink">{item.title}</p>
                {item.description && (
                  <p className="mt-0.5 text-xs text-ink-faint">{item.description}</p>
                )}
              </div>
              {item.action && (
                <button
                  type="button"
                  onClick={() => {
                    item.action?.onClick();
                    dismiss(item.id);
                  }}
                  className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-1 text-xs font-medium text-accent-ink hover:bg-accent-soft"
                >
                  <Undo2 className="size-3.5" />
                  {item.action.label}
                </button>
              )}
              <button
                type="button"
                onClick={() => dismiss(item.id)}
                aria-label={t("copy.7b31a9fc4816")}
                className="shrink-0 rounded p-1 text-ink-faint hover:text-ink"
              >
                <X className="size-3.5" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </section>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast, ToastProvider içinde kullanılmalı.");
  return context;
}
